import type { Selector } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import type { ISalesRecommendation } from "@voith-webshop/core-interfaces";
import { reduce } from "lodash";

// store type
import type { ITreeReducerState } from "@/shop/reducers/partTree/types";
import type { IProactiveSales, ISalesRecommendationState } from "@/shop/reducers/personalized/proactiveSales/types";
import { getCurrentPath } from "@/shop/reducers/selectors/routerSelectors";
import type { IShopRootState } from "@/shop/reducers/types";
import type { IShopFeatures, ShopFeature } from "@/shop/types/personalized";
import type { ILatestOrderDocument, ILatestQuoteDocument, IOrderHistoryDocument } from "@/shop/types/personalized/orderHistory";
import type { IPaperMachineInfo } from "@/shop/types/personalized/originalSupply";
import type { ITreeStructure } from "@/shop/types/tree";

import type { IShopFeatureState } from "./features/types";
import type { IOrderHistoryDetailsState, IOrderHistoryLatestState, IOrderHistoryState } from "./orderHistory/types";
import type { IOriginalSupplyState } from "./originalSupply/types";
import { getPersonalizedAreaRoutingStructure } from "./routingStructure";
import type { IPersonalizedAreaState } from "./types";
import { getIndustryFeatures, getIsLoggedIn } from "../session/selectors";

export const partTreeStateSelector: Selector<IShopRootState, ITreeReducerState> = (state) => state.partTree;
export const partTreesSelector: Selector<IShopRootState, { [treeUrl: string]: ITreeStructure }> = (state) => partTreeStateSelector(state).trees;

export const personalizedAreaStateSelector: Selector<IShopRootState, IPersonalizedAreaState> = (state) => state.personalized;
export const featureStateSelector: Selector<IShopRootState, IShopFeatureState> = (state) => personalizedAreaStateSelector(state).features;
export const originalSupplyStateSelector: Selector<IShopRootState, IOriginalSupplyState> = (state) => personalizedAreaStateSelector(state).originalSupply;

export const orderHistoryStateSelector: Selector<IShopRootState, IOrderHistoryState> = (state) => personalizedAreaStateSelector(state).orderHistory;
export const orderHistoryDetailsStateSelector: Selector<IShopRootState, IOrderHistoryDetailsState> = (state) => orderHistoryStateSelector(state).details;
export const orderHistoryLatestStateSelector: Selector<IShopRootState, IOrderHistoryLatestState> = (state) => orderHistoryStateSelector(state).latest;

export const proactiveSalesSelector: Selector<IShopRootState, IProactiveSales> = (state) => personalizedAreaStateSelector(state).proactiveSales;
export const salesRecommendationStateSelector: Selector<IShopRootState, ISalesRecommendationState> = (state) =>
  proactiveSalesSelector(state).salesRecommendation;

// personalized area features

/**
 * Indicates which features should be shown in personalized area
 *
 * Merges features
 * - detected by rest: contracts, original supply
 * - determined by industry: news, related products
 */
export const getPersonalizedFeatures: Selector<IShopRootState, IShopFeatures> = createSelector(
  getIsLoggedIn,
  featureStateSelector,
  getIndustryFeatures,
  (isLoggedIn, detectedFeatureState, industryFeatures): IShopFeatures => ({
    ...(detectedFeatureState.data || ({} as IShopFeatures)),
    ...industryFeatures,
    hasOrderHistory: isLoggedIn
  })
);

/**
 * Returns a list with personalized features that are enabled
 */
export const getPersonalizedFeatureList = createSelector(getPersonalizedFeatures, (shopFeatures) =>
  reduce(
    shopFeatures,
    (featureList, featureEnabled, featureId) => (featureEnabled ? featureList.concat(featureId as ShopFeature) : featureList),
    [] as ShopFeature[]
  )
);

export const hasIbaseEquipmentStructure = createSelector(getPersonalizedFeatures, (state) => state?.hasIbaseEquipmentStructure ?? true); // fallback to true (equipmentStructure will only be hidden if this flag exists and is false #136903)

export const hasCustomerMaterialsReport = createSelector(getPersonalizedFeatures, (state) => state?.hasCustomerMaterialsReport);

// original supply

/** Provides the list of machines for original supply  */
export const getMachines: Selector<IShopRootState, IPaperMachineInfo[]> = createSelector(originalSupplyStateSelector, (state) => state.machines);

/** Provides the current machinenumber based on app location */
export const getSelectedMachineNumber: Selector<IShopRootState, string> = createSelector(
  getPersonalizedAreaRoutingStructure,
  getCurrentPath,
  (paStructure, currentPath) => {
    const basePath: string = paStructure.basePath.path;
    const subPath: string = paStructure.originalSupply.path;

    const path: string = currentPath;
    const pathElems = path.split("/").filter((e) => e && `/${e}` !== basePath && `/${e}` !== subPath);
    const machineNumber = pathElems[0] ? pathElems[0] : "";
    return machineNumber;
  }
);

export const getSelectedMachine: Selector<IShopRootState, IPaperMachineInfo> = createSelector(
  getMachines,
  getSelectedMachineNumber,
  (machines, machineNumber) => {
    return machines.filter((m) => m.machineNumber === machineNumber)[0]!;
  }
);

// order history

export const getOrderHistoryDocument: Selector<IShopRootState, IOrderHistoryDocument> = createSelector(orderHistoryDetailsStateSelector, (state) => state.data);

export const getOrderHistoryDocumentIsFetching: Selector<IShopRootState, boolean> = createSelector(
  orderHistoryDetailsStateSelector,
  (state) => state.isFetching
);

export const getOrderHistoryDocumentHasError: Selector<IShopRootState, boolean> = createSelector(orderHistoryDetailsStateSelector, (state) => state.hasError);

export const getLatestOrders: Selector<IShopRootState, ILatestOrderDocument[]> = createSelector(orderHistoryLatestStateSelector, (state) =>
  state.data ? state.data.orders.slice(0, 5) : []
);

export const getLatestQuotes: Selector<IShopRootState, ILatestQuoteDocument[]> = createSelector(orderHistoryLatestStateSelector, (state) =>
  state.data ? state.data.quotes.slice(0, 5) : []
);

export const getQuotesCount: Selector<IShopRootState, number> = createSelector(orderHistoryLatestStateSelector, (state) =>
  state.data ? state.data.numQuotes : 0
);

export const getOrdersCount: Selector<IShopRootState, number> = createSelector(orderHistoryLatestStateSelector, (state) =>
  state.data ? state.data.numOrders : 0
);

// salesRecommendation

export const getSalesRecommendation: Selector<IShopRootState, ISalesRecommendation[]> = createSelector(salesRecommendationStateSelector, (state) =>
  state ? state.data : []
);
export const getSalesRecommendationIsFetching: Selector<IShopRootState, boolean> = createSelector(
  salesRecommendationStateSelector,
  (state) => state.isFetching
);

export const getSalesRecommendationHasError: Selector<IShopRootState, boolean> = createSelector(salesRecommendationStateSelector, (state) => state.hasError);

// routing

/** Provides the path from machinenumber onwards */
export const getRelativeMachinePath: Selector<IShopRootState, string> = createSelector(
  getPersonalizedAreaRoutingStructure,
  getCurrentPath,
  (paStructure, currentPath) => {
    const basePath: string = paStructure.basePath.path;
    const originalSupplyPath: string = paStructure.originalSupply.path;

    return currentPath
      .split("/")
      .filter((e) => e && `/${e}` !== basePath && `/${e}` !== originalSupplyPath)
      .reduce((res, next) => `${res}/${next}`);
  }
);

// getItemNavigationElems
export const getItemPathElems: Selector<IShopRootState, string[]> = createSelector(
  getPersonalizedAreaRoutingStructure,
  getCurrentPath,
  getSelectedMachineNumber,
  (paStructure, currentPath, machineNumber) => {
    const basePath: string = paStructure.basePath.path;
    const originalSupplyPath: string = paStructure.originalSupply.path;
    const searchInStructurePath: string = paStructure.searchInStructure.path;

    return currentPath
      .split("/")
      .filter(
        (e) => e && `/${e}` !== basePath && `/${e}` !== originalSupplyPath && `/${e}` !== searchInStructurePath && e !== encodeURIComponent(machineNumber)
      );
  }
);

function findObjInTree(obj: ITreeStructure, key: string): ITreeStructure {
  return obj.items.find((o) => o.id === key)!;
}

function findLabel(obj: ITreeStructure, keys: string[]): string {
  let nextItem: ITreeStructure = obj;

  for (const i of keys) {
    nextItem = findObjInTree(nextItem, i);
  }
  return nextItem.label;
}

export const getCurrentItemLabel: Selector<IShopRootState, string> = createSelector(
  getSelectedMachine,
  getItemPathElems,
  partTreesSelector,
  (machine, itemPathElems, trees) => {
    let label = "";

    if (machine && trees && Object.keys(trees).length > 0) {
      const url: string = Object.keys(trees).filter((treeKey) => trees[treeKey] && trees[treeKey].id === machine.machineNumber)[0]!;
      const tree = trees[url];

      if (tree) {
        label = findLabel(tree.items[0]!, itemPathElems);
      }
    }
    return label;
  }
);

export const getPreviousItemLabel: Selector<IShopRootState, string> = createSelector(
  getSelectedMachine,
  getItemPathElems,
  partTreesSelector,
  (machine, itemPathElems, trees) => {
    let label = "";

    if (machine && trees && Object.keys(trees).length > 0) {
      const url: string = Object.keys(trees).filter((treeKey) => trees[treeKey] && trees[treeKey].id === machine.machineNumber)[0]!;
      const tree = trees[url];

      if (tree && itemPathElems.length >= 1) {
        label = findLabel(tree.items[0]!, itemPathElems.slice(0, itemPathElems.length - 1));
      }
    }
    return label;
  }
);

export const isLocationOnSearchInStructurePath: Selector<IShopRootState, boolean> = createSelector(
  getRelativeMachinePath,
  getSelectedMachineNumber,
  (machinePath, machineNumber) => {
    const pathElems = machinePath.split("/").filter((e) => e && `/${e}` !== machineNumber);

    // TODO: get string from defined const
    return pathElems[1] === "search-in-structure";
  }
);

export const isLocationOnSearchInMachinePath: Selector<IShopRootState, boolean> = createSelector(
  getRelativeMachinePath,
  getSelectedMachineNumber,
  (machinePath, machineNumber) => {
    const pathElems = machinePath.split("/").filter((e) => e && `/${e}` !== machineNumber);

    // TODO: get string from defined const
    // console.log("machine pathElems: ", pathElems);
    return pathElems[1] === "search-in-machine";
  }
);
